import {
  ACCEL_AIR,
  ACCEL_GROUND,
  CAPSULE_HEIGHT_CROUCH,
  CAPSULE_HEIGHT_STAND,
  CAPSULE_RADIUS,
  CROUCH_SPEED,
  EYE_HEIGHT_CROUCH,
  EYE_HEIGHT_STAND,
  FRICTION_GROUND,
  GRAVITY,
  JUMP_VELOCITY,
  NOCLIP_SPEED,
  NOCLIP_SPRINT_MULT,
  SPRINT_SPEED,
  STAMINA_MAX,
  STAMINA_REGEN,
  STAMINA_SPRINT_DRAIN,
  STEP_UP_MAX,
  WALK_SPEED,
} from "../constants.js";
import type { Vec3 } from "../math/vec3.js";
import { BTN, type InputCmd, type Stance } from "../protocol/messages.js";
import type { CollisionWorld } from "./collision.js";

/**
 * THE shared, deterministic movement step: server and client call this exact
 * function. Pure — no mutation, no clock, no shared allocation, fixed float
 * order — so the same inputs give bit-identical output. `dt` must always be
 * `TICK_DT`.
 */

export interface MoveState {
  /** Feet position (capsule bottom). */
  pos: Vec3;
  vel: Vec3;
  onGround: boolean;
  stance: Stance;
  stamina: number;
}

/**
 * Movement modifiers supplied by the caller (encumbrance, injury, …).
 *
 *   speedMult  scales the TARGET speed — a blacked leg: you jog, but start and
 *              stop like yourself.
 *   accelMult  scales `ACCEL_GROUND`, `ACCEL_AIR` AND `FRICTION_GROUND`, so a
 *              loaded player is slow to get going and slow to stop — mass as
 *              momentum, not a second speed cap.
 *
 * `accelMult === 1` is BIT-IDENTICAL to the pre-encumbrance step (×1.0 is the
 * identity). Both are REQUIRED: an optional one would let a caller silently
 * opt out, and which mods were in force is the one thing both sides may never
 * disagree about.
 */
export interface MoveMods {
  speedMult: number;
  accelMult: number;
  /**
   * Scales the STAMINA REGEN TERM only — the endurance skill
   * (`SkillMods.staminaRegenMult`): a budget that refills faster, never a
   * sprint that costs less. Optional, unlike the two above: an absent value is
   * the identity, and a caller opting out recovers a number only its owner
   * sees.
   */
  staminaRegenMult?: number;
}

/** Horizontal speed under which ground friction snaps you to a full stop. */
const STOP_SPEED = 0.05;
const STOP_SPEED_SQ = STOP_SPEED * STOP_SPEED;

/** Tolerance for "the collision resolver did not move me". */
const CONTACT_EPS = 1e-4;

/** Bisection steps used to settle onto a ledge after a step-up. Fixed count
 *  (not a tolerance loop) so the result is deterministic. */
const STEP_DOWN_ITERATIONS = 10;

/**
 * Heights probed inside `STEP_UP_MAX` when the FULL lift is blocked (a ceiling
 * over a staircase). Fixed count, so the search is deterministic.
 */
const STEP_PROBE_SAMPLES = 5;

/**
 * Longest displacement resolved in one go; further is split into equal substeps
 * so a fast fall cannot sink into thin geometry. Above the fastest normal step
 * (~0.217 m), so ordinary movement stays a single, bit-for-bit unchanged
 * resolve.
 */
const SUBSTEP_MAX = CAPSULE_RADIUS * 0.75;

/** Hard cap on substeps, so an absurd velocity cannot stall a tick. */
const MAX_SUBSTEPS = 16;

export function capsuleHeightFor(stance: Stance): number {
  return stance === "crouch" ? CAPSULE_HEIGHT_CROUCH : CAPSULE_HEIGHT_STAND;
}

export function eyeHeightFor(stance: Stance): number {
  return stance === "crouch" ? EYE_HEIGHT_CROUCH : EYE_HEIGHT_STAND;
}

/** Convenience: eye position for a feet position + stance. */
export function eyePosition(pos: Vec3, stance: Stance): Vec3 {
  return { x: pos.x, y: pos.y + eyeHeightFor(stance), z: pos.z };
}

export function stepMovement(
  s: MoveState,
  cmd: InputCmd,
  world: CollisionWorld,
  mods: MoveMods,
  dt: number,
): MoveState {
  const btn = cmd.buttons;
  const radius = CAPSULE_RADIUS;

  // -------------------------------------------------------------------
  // 0. Noclip — the F6 debug fly mode. Inside the shared step because the
  //    flying thing is the PLAYER: the server integrates the same branch off
  //    the same button bit, so there is nothing to reconcile. Skips gravity,
  //    collision, substeps, step-up and `mods`. Velocity is the fly velocity,
  //    so releasing the bit hands gravity a real state to fall from.
  // -------------------------------------------------------------------
  if ((btn & BTN.NOCLIP) !== 0) {
    // Both angles come straight off the wire; a non-finite one would turn the
    // whole state to NaN and there is no recovering from that in a replay.
    const nYaw = Number.isFinite(cmd.yaw) ? cmd.yaw : 0;
    const nPitch = Number.isFinite(cmd.pitch) ? cmd.pitch : 0;
    const sy = Math.sin(nYaw);
    const cy = Math.cos(nYaw);
    const sp = Math.sin(nPitch);
    const cp = Math.cos(nPitch);
    // Unlike normal movement, the direction takes PITCH into account: looking
    // up and holding forward is how you climb out through the roof.
    const nFwdX = -sy * cp;
    const nFwdY = sp;
    const nFwdZ = -cy * cp;
    const nRightX = cy;
    const nRightZ = -sy;

    const moveF = ((btn & BTN.FWD) !== 0 ? 1 : 0) - ((btn & BTN.BACK) !== 0 ? 1 : 0);
    const moveR = ((btn & BTN.RIGHT) !== 0 ? 1 : 0) - ((btn & BTN.LEFT) !== 0 ? 1 : 0);
    const moveU = ((btn & BTN.JUMP) !== 0 ? 1 : 0) - ((btn & BTN.CROUCH) !== 0 ? 1 : 0);

    let wx = nFwdX * moveF + nRightX * moveR;
    let wy = nFwdY * moveF + moveU;
    let wz = nFwdZ * moveF + nRightZ * moveR;
    const wLen = Math.sqrt(wx * wx + wy * wy + wz * wz);
    if (wLen > 1e-6) {
      // Normalising keeps diagonal flight from being faster than straight
      // flight, and keeps NOCLIP_SPEED an honest m/s figure.
      const speed =
        NOCLIP_SPEED * ((btn & BTN.SPRINT) !== 0 ? NOCLIP_SPRINT_MULT : 1);
      const k = speed / wLen;
      wx *= k;
      wy *= k;
      wz *= k;
    } else {
      // No input is a dead stop, not a coast: you hover exactly where you are.
      wx = 0;
      wy = 0;
      wz = 0;
    }

    let nStamina = s.stamina + STAMINA_REGEN * dt;
    if (nStamina > STAMINA_MAX) nStamina = STAMINA_MAX;

    return {
      pos: { x: s.pos.x + wx * dt, y: s.pos.y + wy * dt, z: s.pos.z + wz * dt },
      vel: { x: wx, y: wy, z: wz },
      onGround: false,
      // Preserved rather than derived: while flying, CROUCH means "down", so
      // reading it as a stance change would squash you every time you descend.
      stance: s.stance,
      stamina: nStamina,
    };
  }

  // -------------------------------------------------------------------
  // 1. Stance. CROUCH is hold-to-crouch; standing back up requires headroom.
  // -------------------------------------------------------------------
  let stance: Stance = s.stance;
  if ((btn & BTN.CROUCH) !== 0) {
    stance = "crouch";
  } else if (s.stance === "crouch") {
    const test = world.resolveCapsule(s.pos, radius, CAPSULE_HEIGHT_STAND);
    if (
      Math.abs(test.x - s.pos.x) <= CONTACT_EPS &&
      Math.abs(test.y - s.pos.y) <= CONTACT_EPS &&
      Math.abs(test.z - s.pos.z) <= CONTACT_EPS
    ) {
      stance = "stand";
    }
  }
  const height = capsuleHeightFor(stance);

  // -------------------------------------------------------------------
  // 2. Wish direction — yaw only. A non-finite `yaw` off the wire would turn
  //    the whole state to NaN.
  // -------------------------------------------------------------------
  const yaw = Number.isFinite(cmd.yaw) ? cmd.yaw : 0;
  const sinYaw = Math.sin(yaw);
  const cosYaw = Math.cos(yaw);
  // Three.js convention: yaw 0 looks down -Z, +yaw rotates counter-clockwise.
  const fwdX = -sinYaw;
  const fwdZ = -cosYaw;
  const rightX = cosYaw;
  const rightZ = -sinYaw;

  const moveF = ((btn & BTN.FWD) !== 0 ? 1 : 0) - ((btn & BTN.BACK) !== 0 ? 1 : 0);
  const moveR = ((btn & BTN.RIGHT) !== 0 ? 1 : 0) - ((btn & BTN.LEFT) !== 0 ? 1 : 0);

  let wishX = fwdX * moveF + rightX * moveR;
  let wishZ = fwdZ * moveF + rightZ * moveR;
  const wishLen = Math.sqrt(wishX * wishX + wishZ * wishZ);
  const hasInput = wishLen > 1e-6;
  if (hasInput) {
    const inv = 1 / wishLen;
    wishX *= inv;
    wishZ *= inv;
  } else {
    wishX = 0;
    wishZ = 0;
  }

  // -------------------------------------------------------------------
  // 3. Target speed.
  // -------------------------------------------------------------------
  const sprinting =
    hasInput &&
    (btn & BTN.SPRINT) !== 0 &&
    (btn & BTN.FWD) !== 0 &&
    s.stamina > 0 &&
    stance === "stand";

  const baseSpeed =
    stance === "crouch" ? CROUCH_SPEED : sprinting ? SPRINT_SPEED : WALK_SPEED;
  const speed = baseSpeed * mods.speedMult;

  // -------------------------------------------------------------------
  // 4. Horizontal acceleration / friction.
  // -------------------------------------------------------------------
  let vx = s.vel.x;
  let vy = s.vel.y;
  let vz = s.vel.z;

  if (s.onGround) {
    if (hasInput) {
      // Move toward the target velocity at a capped rate — speeding up and
      // slowing down in one branch, converging exactly on `speed`.
      let dx = wishX * speed - vx;
      let dz = wishZ * speed - vz;
      const dLen = Math.sqrt(dx * dx + dz * dz);
      const maxDelta = ACCEL_GROUND * mods.accelMult * dt;
      if (dLen > maxDelta && dLen > 0) {
        const k = maxDelta / dLen;
        dx *= k;
        dz *= k;
      }
      vx += dx;
      vz += dz;
    } else {
      // Friction scaled by the SAME `accelMult` as acceleration — see
      // `MoveMods`.
      const k = 1 - FRICTION_GROUND * mods.accelMult * dt;
      const keep = k > 0 ? k : 0;
      vx *= keep;
      vz *= keep;
      if (vx * vx + vz * vz < STOP_SPEED_SQ) {
        vx = 0;
        vz = 0;
      }
    }
  } else if (hasInput) {
    // Weak air control that can add speed along the wish direction but never
    // removes existing momentum.
    const current = vx * wishX + vz * wishZ;
    const addSpeed = speed - current;
    if (addSpeed > 0) {
      const accel = ACCEL_AIR * mods.accelMult * dt;
      const a = accel < addSpeed ? accel : addSpeed;
      vx += wishX * a;
      vz += wishZ * a;
    }
  }

  // -------------------------------------------------------------------
  // 5. Jump + gravity.
  // -------------------------------------------------------------------
  let jumped = false;
  if (s.onGround && (btn & BTN.JUMP) !== 0) {
    vy = JUMP_VELOCITY;
    jumped = true;
  }
  vy -= GRAVITY * dt;

  // -------------------------------------------------------------------
  // 6. Integrate and resolve. Displacements above `SUBSTEP_MAX` are split into
  //    equal substeps; at normal speeds this is one substep, float for float
  //    the original resolve.
  // -------------------------------------------------------------------
  const stepX = vx * dt;
  const stepY = vy * dt;
  const stepZ = vz * dt;
  const travel = Math.sqrt(stepX * stepX + stepY * stepY + stepZ * stepZ);
  let substeps = 1;
  if (travel > SUBSTEP_MAX) {
    substeps = Math.ceil(travel / SUBSTEP_MAX);
    if (!(substeps <= MAX_SUBSTEPS)) substeps = MAX_SUBSTEPS;
  }
  const sdt = dt / substeps;

  let ix = s.pos.x;
  let iy = s.pos.y;
  let iz = s.pos.z;
  let rx = s.pos.x;
  let ry = s.pos.y;
  let rz = s.pos.z;
  for (let i = 0; i < substeps; i++) {
    ix += vx * sdt;
    iy += vy * sdt;
    iz += vz * sdt;
    rx += vx * sdt;
    ry += vy * sdt;
    rz += vz * sdt;
    const r = world.resolveCapsule({ x: rx, y: ry, z: rz }, radius, height);
    rx = r.x;
    ry = r.y;
    rz = r.z;
  }

  let resolved: Vec3 = { x: rx, y: ry, z: rz };
  let steppedUp = false;

  // -------------------------------------------------------------------
  // 7. Step-up: if a horizontal push-out blocked us while grounded, try to
  //    climb the obstacle instead of grinding against it.
  // -------------------------------------------------------------------
  const blockedHorizontally =
    hasInput &&
    (Math.abs(resolved.x - ix) > CONTACT_EPS ||
      Math.abs(resolved.z - iz) > CONTACT_EPS);

  if (blockedHorizontally && s.onGround && !jumped) {
    const baseY = s.pos.y;
    const liftY = baseY + STEP_UP_MAX;
    let lo = baseY;
    let hi = liftY;
    let found = false;

    if (isClearAt(world, ix, liftY, iz, radius, height)) {
      found = true;
      if (isClearAt(world, ix, lo, iz, radius, height)) hi = lo;
    } else {
      // The full lift is blocked (a ceiling over a staircase): probe a fixed
      // ladder of heights and take the lowest fit.
      let prev = baseY;
      for (let k = 1; k < STEP_PROBE_SAMPLES; k++) {
        const y = baseY + (STEP_UP_MAX * k) / STEP_PROBE_SAMPLES;
        if (isClearAt(world, ix, y, iz, radius, height)) {
          lo = prev;
          hi = y;
          found = true;
          break;
        }
        prev = y;
      }
    }

    if (found) {
      // Settle back down to the lowest height at which the capsule is still
      // clear: that is the top of the step (or the edge it perches on).
      if (hi > lo) {
        for (let i = 0; i < STEP_DOWN_ITERATIONS; i++) {
          const mid = (lo + hi) * 0.5;
          if (isClearAt(world, ix, mid, iz, radius, height)) hi = mid;
          else lo = mid;
        }
      }
      const stepped = world.resolveCapsule({ x: ix, y: hi, z: iz }, radius, height);
      if (
        Math.abs(stepped.x - ix) <= CONTACT_EPS &&
        Math.abs(stepped.z - iz) <= CONTACT_EPS
      ) {
        resolved = stepped;
        steppedUp = true;
        if (vy < 0) vy = 0;
      }
    }
  }

  // -------------------------------------------------------------------
  // 8. Kill velocity that is driving into a surface, so it cannot accumulate.
  // -------------------------------------------------------------------
  if (!steppedUp) {
    if ((resolved.x - ix) * vx < 0) vx = 0;
    if ((resolved.z - iz) * vz < 0) vz = 0;
  }
  if ((resolved.y - iy) * vy < 0) vy = 0;

  // -------------------------------------------------------------------
  // 9. Ground state.
  // -------------------------------------------------------------------
  const onGround = world.groundCheck(resolved, radius);
  if (onGround && vy < 0) vy = 0;

  // -------------------------------------------------------------------
  // 10. Stamina. `mods.staminaRegenMult` touches the regen half only; absent
  //     is 1, the identity.
  // -------------------------------------------------------------------
  const regenMult = mods.staminaRegenMult ?? 1;
  let stamina =
    s.stamina + (sprinting ? -STAMINA_SPRINT_DRAIN : STAMINA_REGEN * regenMult) * dt;
  if (stamina < 0) stamina = 0;
  else if (stamina > STAMINA_MAX) stamina = STAMINA_MAX;

  return {
    pos: { x: resolved.x, y: resolved.y, z: resolved.z },
    vel: { x: vx, y: vy, z: vz },
    onGround,
    stance,
    stamina,
  };
}

/** True when a capsule placed at (x, y, z) is not intersecting anything. */
function isClearAt(
  world: CollisionWorld,
  x: number,
  y: number,
  z: number,
  radius: number,
  height: number,
): boolean {
  const r = world.resolveCapsule({ x, y, z }, radius, height);
  return (
    Math.abs(r.x - x) <= CONTACT_EPS &&
    Math.abs(r.y - y) <= CONTACT_EPS &&
    Math.abs(r.z - z) <= CONTACT_EPS
  );
}
